import { PieChart } from 'lucide-react';

import { DonutChart, type DonutSlice } from '@/components/ui/DonutChart';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency } from '@/lib/format';
import type { FeeLedgerTotals, FeeStatus } from '@/types/fees';

export interface FeeCollectionOverviewProps {
  /** The session's own totals — never the filtered table's. */
  totals: FeeLedgerTotals | null;
  /** What is still outstanding under each standing, as decimal strings. */
  balanceByStatus: Record<FeeStatus, string> | null;
  isLoading: boolean;
}

/**
 * Where the session's money is, as one ring.
 *
 * The ring divides what the institute is owed for the year — nothing more, so
 * the three arcs always add up to the figure in the middle:
 *
 *   collected     every rupee taken, whoever paid it and whether or not their
 *                 account is now settled.
 *   part paid     what the families who have paid *something* still owe.
 *   not paid      what the families who have paid nothing owe.
 *
 * Accounts with no fees agreed are not in it: they are charged nothing, so they
 * are worth nothing on either side of the ring. They are still counted on the
 * register's own "No fees set" tab, which is where that gap gets closed.
 *
 * Deliberately the whole session rather than the filtered table. This is the
 * figure an office quotes to a trustee, and a headline that quietly followed a
 * class filter is how the wrong number gets quoted.
 */
export function FeeCollectionOverview({
  totals,
  balanceByStatus,
  isLoading,
}: FeeCollectionOverviewProps) {
  const receivable = Number(totals?.payable ?? 0);

  const slices: DonutSlice[] = [
    {
      key: 'collected',
      label: 'Collected',
      value: Number(totals?.collected ?? 0),
      stroke: 'stroke-emerald-500',
      dot: 'bg-emerald-500',
    },
    {
      key: 'partial',
      label: 'Part paid',
      value: Number(balanceByStatus?.PARTIAL ?? 0),
      stroke: 'stroke-amber-500',
      dot: 'bg-amber-500',
    },
    {
      key: 'unpaid',
      label: 'Not paid',
      value: Number(balanceByStatus?.UNPAID ?? 0),
      stroke: 'stroke-rose-500',
      dot: 'bg-rose-500',
    },
  ];

  return (
    <section className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-5 shadow-xs">
      <div className="flex items-center gap-2.5  ">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400"
          aria-hidden
        >
          <PieChart className="h-4 w-4" />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-base font-extrabold text-slate-900 dark:text-white tracking-tight">
          Fee collection overview
        </h2>
      </div>

      {isLoading || !totals || !balanceByStatus ? (
        <div className="mt-4 flex flex-col items-center gap-5 sm:flex-row">
          <Skeleton className="h-36 w-36 shrink-0 rounded-full" />
          <div className="flex w-full flex-col gap-2.5">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-4 w-full rounded-xl" />
            ))}
          </div>
        </div>
      ) : receivable <= 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-8 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
          Nothing has been charged for this session yet, so there is nothing to collect.
        </p>
      ) : (
        <DonutChart
          className="mt-4"
          slices={slices}
          total={receivable}
          caption="Total receivable"
          formatValue={(value) => formatCurrency(value)}
        />
      )}
    </section>
  );
}
